import { callAPI } from "../../helper/apiUtils";
import { loadAllRooms } from "./components/CockpitTodoLists";

// Per daily report: which priorities were already mirrored into tasks and
// which ones the user deleted, stored as
// `{ id: reportId, todos: [{ id, text }], dismissed: [text] }`. The text lets
// the report mark a priority as noted; the id lets the cockpit keep listing the
// task after it was renamed. Without the record a deleted task would come back
// on reload.
const STORE_KEY = "kjuup.cockpit.priorityBookmarks";
const STORE_LIMIT = 50;
const inFlight = new Set();

export function priorityText(row) {
  return String((row && (row.nextStep || row.project)) || "").replace(/\s+/g, " ").trim();
}

export function normalizeTaskText(value) {
  return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** Words too common to tell two priorities — or two sources — apart. */
export const STOP_WORDS = new Set([
  "diese", "dieser", "dieses", "nicht", "oder", "eine", "einer", "einem", "einen", "nach",
  "fuer", "für", "dass", "wird", "werden", "sind", "soll", "sollte", "bitte", "noch", "auch",
  "dann", "wenn", "aber", "über", "ueber", "beim", "sich", "that", "this", "with", "from",
  "have", "been", "your", "their", "will", "should", "about", "please",
]);

function fold(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

// Function words the list above does not carry — it was written for matching a
// priority against its source message, where they are rare enough to ignore.
// Comparing two priorities they are everywhere, so they have to go. Anything
// shorter than three letters is dropped by the tokenizer anyway.
const FILLER = [
  "und", "der", "die", "das", "den", "dem", "des", "ein", "als", "auf", "aus", "bei", "bis",
  "ist", "mit", "von", "vom", "vor", "zum", "zur", "wie", "wir", "uns", "ihr", "ihre", "ihren",
  "sein", "seine", "hat", "haben", "kann", "koennen", "muss", "muessen", "damit", "sowie",
  "alle", "allen", "jede", "jeden", "bzw", "ggf", "inkl",
  "and", "the", "for", "are", "was", "were", "into", "per", "via", "its", "our", "you", "all", "any",
];

const FOLDED_STOP = new Set([...STOP_WORDS, ...FILLER].map(fold));

// Long words are cut to a common prefix so inflections line up ("Rechnung" /
// "Rechnungen"). Numbers are kept whole: "Bauabschnitt 2" and "Bauabschnitt 3"
// are different steps.
const STEM_LENGTH = 5;

/** The content words of a priority, folded and stemmed. */
export function priorityTokens(text) {
  return new Set(
    fold(text)
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => (word.length >= 3 || /\d/.test(word)) && !FOLDED_STOP.has(word))
      .map((word) => (/\d/.test(word) || word.length <= STEM_LENGTH ? word : word.slice(0, STEM_LENGTH)))
  );
}

// Sørensen-Dice over the token sets. A rewording keeps almost every content
// word, so the bar is high enough that two different steps stay apart.
const SIMILAR_ENOUGH = 0.8;
// The shorter text must say enough to stand for the longer one, and the longer
// one must not add a second step's worth of words on top of it.
const CONTAINED_MIN_TOKENS = 3;
const CONTAINED_MAX_RATIO = 2;

function tokensOf(value) {
  return value instanceof Set ? value : priorityTokens(value);
}

export function prioritySimilarity(a, b) {
  const left = tokensOf(a);
  const right = tokensOf(b);
  if (!left.size || !right.size) return 0;
  let shared = 0;
  left.forEach((token) => {
    if (right.has(token)) shared += 1;
  });
  // The same step once spelled out and once shortened: every word of the
  // shorter one is in the longer one.
  const smaller = Math.min(left.size, right.size);
  const larger = Math.max(left.size, right.size);
  if (shared === smaller && smaller >= CONTAINED_MIN_TOKENS && larger <= smaller * CONTAINED_MAX_RATIO) return 1;
  return (2 * shared) / (left.size + right.size);
}

export function isSamePriority(a, b) {
  const left = normalizeTaskText(a);
  const right = normalizeTaskText(b);
  if (!left || !right) return false;
  return left === right || prioritySimilarity(left, right) >= SIMILAR_ENOUGH;
}

/**
 * The entry of `list` describing the same priority as `text`, or null.
 * `pick` reads an entry's text; without it the entries are the texts.
 */
export function findSimilar(list, text, pick) {
  const key = normalizeTaskText(text);
  if (!key) return null;
  const tokens = priorityTokens(key);
  let best = null;
  let bestScore = 0;
  (list || []).forEach((entry) => {
    if (!entry) return;
    const other = normalizeTaskText(pick ? pick(entry) : entry);
    if (!other) return;
    const score = other === key ? 1 : prioritySimilarity(tokens, priorityTokens(other));
    if (score >= SIMILAR_ENOUGH && score > bestScore) {
      best = entry;
      bestScore = score;
    }
  });
  return best;
}

/** Reads the free-text deadline a report writes into a priority row. */
export function parsePriorityDeadline(raw) {
  const s = String(raw || "").replace(/\s+/g, " ").trim();
  if (!s || /^[-–—]+$/.test(s) || /^(keine|none|n\/a|offen)$/i.test(s)) return null;
  const lower = s.toLowerCase();
  const now = new Date();
  if (/^(heute|today)$/.test(lower)) return now.toISOString();
  if (/^(morgen|tomorrow)$/.test(lower)) {
    const next = new Date(now);
    next.setDate(next.getDate() + 1);
    return next.toISOString();
  }
  const full = s.match(/(\d{1,2})\.(\d{1,2})\.(\d{2,4})/);
  if (full) {
    const year = full[3].length === 2 ? 2000 + Number(full[3]) : Number(full[3]);
    const date = new Date(year, Number(full[2]) - 1, Number(full[1]), 9, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  const short = s.match(/(\d{1,2})\.(\d{1,2})\.?$/);
  if (short) {
    const date = new Date(now.getFullYear(), Number(short[2]) - 1, Number(short[1]), 9, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return null;
}

/** The report's deadline as a value an `<input type="date">` accepts. */
/**
 * The categories the server sorts a task under (`bookmarkCategory`,
 * BookmarkTodoClassifierService); an unsorted one counts as "other".
 */
export const BOOKMARK_CATEGORIES = ["appointment", "critical", "decision", "other"];

/**
 * The cockpit's two headers, in display order, matching its two tiles:
 * "Fristen & Aufgaben" (critical points, decisions and every other task) and
 * "Termine". Within "tasks" the categories keep this order for equal deadlines.
 */
export const COCKPIT_TASK_GROUPS = [
  { key: "tasks", categories: ["critical", "decision", "other"] },
  { key: "appointment", categories: ["appointment"] },
];

export function cockpitGroupOf(category) {
  return category === "appointment" ? "appointment" : "tasks";
}

export function cockpitGroupTitles(strings = {}) {
  return {
    tasks: strings.cockpit_group_tasks || "Fristen & Aufgaben",
    appointment: strings.cockpit_group_appointments || "Termine",
  };
}

/** A date as the local "YYYY-MM-DD", else "". */
export function cockpitDayKey(value) {
  const date = new Date(value || "");
  if (!value || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The day a cockpit task falls on: a dated task its date, a bookmark its deadline, else "". */
export function cockpitTaskDay(task) {
  if (!task) return "";
  if (task.isBookmarked != null && !Number(task.isBookmarked)) return cockpitDayKey(task.createdAt);
  return String(task.bookmarkDeadline || "").slice(0, 10);
}

export function priorityDateValue(row) {
  const iso = parsePriorityDeadline(row && row.deadline);
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function readStore() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((entry) => entry && entry.id) : [];
  } catch (_) {
    return [];
  }
}

function writeStore(entries) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(entries.slice(0, STORE_LIMIT)));
  } catch (_) {
    /* without storage the text dedupe below still prevents duplicates */
  }
}

function readEntry(reportId) {
  if (!reportId) return null;
  const hit = readStore().find((entry) => entry.id === String(reportId));
  if (!hit) return null;
  return {
    todos: Array.isArray(hit.todos) ? hit.todos : [],
    dismissed: Array.isArray(hit.dismissed) ? hit.dismissed : [],
  };
}

function writeEntry(reportId, state) {
  const id = String(reportId);
  writeStore([{ id, ...state }, ...readStore().filter((entry) => entry.id !== id)]);
}

function statePath(reportId) {
  return `/user/cockpit/report-priorities/${encodeURIComponent(String(reportId))}`;
}

function asState(payload) {
  const data = payload && payload.data;
  if (!data || payload.status === false) return null;
  return {
    todos: Array.isArray(data.todos) ? data.todos : [],
    dismissed: Array.isArray(data.dismissed) ? data.dismissed : [],
  };
}

/**
 * The record for a report: which priorities already produced a task and which
 * ones were deleted. It is kept on the server so the list is the same on every
 * device; the local copy only stands in while the request is out or failed.
 */
async function loadState(reportId) {
  const local = readEntry(reportId) || { todos: [], dismissed: [] };
  if (!reportId) return local;
  try {
    const { data } = await callAPI(statePath(reportId), {}, "GET");
    const state = asState(data);
    if (!state) return local;
    // Anything the browser holds that the server does not is handed up rather
    // than dropped: a write that failed earlier, or a record from back when
    // this lived in local storage only.
    const newDismissed = local.dismissed.filter((text) => !findSimilar(state.dismissed, text));
    const newTodos = local.todos.filter((entry) => entry && !findSimilar(state.todos, entry.text, (t) => t.text));
    if (newDismissed.length || newTodos.length) {
      return saveState(reportId, { todos: state.todos.concat(newTodos), dismissed: newDismissed });
    }
    writeEntry(reportId, state);
    return state;
  } catch (_) {
    return local;
  }
}

/**
 * Stores the record. `todos` replaces the mirrored list; `dismissed` is added
 * to what the server already has, so two devices writing at once cannot undo
 * each other's deletions.
 */
async function saveState(reportId, patch) {
  if (!reportId) return { todos: [], dismissed: [] };
  const current = readEntry(reportId) || { todos: [], dismissed: [] };
  const optimistic = {
    todos: patch.todos !== undefined ? patch.todos : current.todos,
    dismissed: current.dismissed.concat(
      (patch.dismissed || []).filter((text) => !findSimilar(current.dismissed, text))
    ),
  };
  optimistic.todos = optimistic.todos.filter((entry) => entry && !findSimilar(optimistic.dismissed, entry.text));
  writeEntry(reportId, optimistic);
  try {
    const { data } = await callAPI(statePath(reportId), {}, "PUT", patch);
    const state = asState(data);
    if (state) {
      writeEntry(reportId, state);
      return state;
    }
  } catch (_) {
    /* the local copy holds the change until the next load retries it */
  }
  return optimistic;
}

/**
 * Takes priorities off the report's list for good, on this device and the
 * next: the sync pass below does not create the step again, and the task they
 * produced stops counting as one of the report's.
 *
 * Takes one text or a whole list. A list goes up in a single write, so
 * clearing a long selection costs one request instead of one per row.
 */
export async function dismissPriority(reportId, text) {
  const keys = (Array.isArray(text) ? text : [text]).map(normalizeTaskText).filter(Boolean);
  const unique = keys.filter((key, index) => keys.indexOf(key) === index);
  if (!reportId || !unique.length) return (readEntry(reportId) || { dismissed: [] }).dismissed;
  const { dismissed } = await saveState(reportId, { dismissed: unique });
  return dismissed;
}

/**
 * Drops a deleted report's sync record. The bookmarked to-dos it produced are
 * left alone — they belong to the user, not to the report.
 */
export async function forgetReport(reportId) {
  if (!reportId) return;
  const id = String(reportId);
  const entries = readStore();
  if (entries.some((entry) => entry.id === id)) writeStore(entries.filter((entry) => entry.id !== id));
  try {
    await callAPI(statePath(id), {}, "DELETE");
  } catch (_) {
    /* the record expires with the report it belongs to */
  }
}

/** Dateless to-dos — the API stores those as bookmarks. */
export async function fetchBookmarkedTodos() {
  const res = await callAPI("/todo/userTodo", {}, "POST", {
    search: "",
    limit: 200,
    page: 1,
    onlyBookmarked: true,
  });
  const payload = res && res.data;
  const items = Array.isArray(payload && payload.data) ? payload.data : [];
  const total = Number(payload && payload.total);
  return { items, total: Number.isFinite(total) ? total : items.length };
}

/**
 * The open tasks a report created. They carry a date, so the bookmark list
 * above does not hold them; they are loaded by the ids the sync recorded.
 */
export async function fetchReportTasks(ids) {
  const itemsOf = (res) => {
    const payload = res && res.data;
    return Array.isArray(payload && payload.data) ? payload.data : [];
  };
  // Every dated task under the cockpit's headers, from this report or an
  // earlier one (done ones of the last two weeks too: their rows drop out).
  const sorted = itemsOf(
    await callAPI("/todo/userTodo", {}, "POST", { search: "", cockpitTasks: true, limit: 200, page: 1 }).catch(() => null)
  ).filter((item) => item && item.bookmarkCategory && !Number(item.isBookmarked));
  const have = new Set(sorted.map((item) => String(item.todoId)));
  // This report's own tasks the classifier has not sorted yet.
  const missing = [...new Set((ids || []).map((id) => String(id || "")).filter(Boolean))].filter((id) => !have.has(id));
  if (!missing.length) return sorted;
  const byId = itemsOf(
    await callAPI("/todo/userTodo", {}, "POST", { search: "", ids: missing, limit: missing.length, page: 1 }).catch(() => null)
  );
  // An API without the `ids` filter answers with the latest tasks instead.
  const wanted = new Set(missing);
  return sorted.concat(byId.filter((item) => item && wanted.has(String(item.todoId)) && !Number(item.isBookmarked)));
}

function roomKey(value) {
  return normalizeTaskText(value).replace(/[^a-z0-9äöüß]/g, "");
}

function roomIdFor(rooms, projectName) {
  const needle = roomKey(projectName);
  if (!needle || needle.length < 3) return null;
  let best = null;
  let bestScore = 0;
  (rooms || []).forEach((room) => {
    const name = roomKey(room && room.name);
    if (!name) return;
    let score = 0;
    if (name === needle) score = 3;
    else if (name.length >= 4 && needle.length >= 4 && (name.includes(needle) || needle.includes(name))) score = 2;
    if (!score) return;
    if (score > bestScore || (score === bestScore && name.length > roomKey(best.name).length)) {
      best = room;
      bestScore = score;
    }
  });
  return best ? best.id || best.buildingId || best._id || null : null;
}

/** The project room of the email or chat the priority was written from. */
function roomIdOf(row, rooms) {
  const direct = row && String(row.roomId || "").trim();
  if (direct) return direct;
  const name = String((row && row.project) || "").trim();
  if (!name || /^(ohne projekt|unassigned|kein projekt|nicht zugeordnet|-)$/i.test(name)) return null;
  return roomIdFor(rooms, name);
}

async function assignRoom(todo, roomId) {
  if (!roomId || !todo || todo.todoId == null) return false;
  if (todo.roomId || todo.buildingList) return false;
  try {
    const { data } = await callAPI(`/todo/${todo.todoId}`, {}, "PUT", { roomId });
    return !(data && data.status === false);
  } catch (_) {
    return false;
  }
}

/** A report row's description text (the part under its title), else "". */
function rowDetails(row) {
  const detail = String((row && row.detail) || "").replace(/\s+/g, " ").trim();
  return detail && normalizeTaskText(detail) !== normalizeTaskText(priorityText(row)) ? detail : "";
}

/**
 * A task made before tasks had a description gets its row's. Only where the
 * server says it has none (`details: null`): an older API sends no field.
 */
async function assignDetails(todo, row) {
  const details = rowDetails(row);
  if (!details || !todo || todo.todoId == null || todo.details !== null) return false;
  try {
    const { data } = await callAPI(`/todo/${todo.todoId}`, {}, "PUT", { details });
    if (data && data.status === false) return false;
    todo.details = details;
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Mirrors a report's priorities into bookmarked tasks by creating them without
 * a date. `keepSummary` stops the API from replacing the priority sentence with
 * a generated one. The pass runs on every load and matches on text, so a
 * report regenerated over an overlapping timeframe links the bookmarks it
 * already has instead of creating near-identical ones.
 */
export async function syncPriorityBookmarks({ report, priorities, rooms, bookmarks, dated }) {
  const reportId = report && report.id;
  if (!reportId) return { created: false, todos: [], dismissed: [] };
  // A remount while the creates are still running would not see the record yet.
  if (inFlight.has(String(reportId))) {
    const local = readEntry(reportId) || { todos: [], dismissed: [] };
    return { created: false, todos: local.todos, dismissed: local.dismissed };
  }

  inFlight.add(String(reportId));
  try {
    const { todos: known, dismissed } = await loadState(reportId);
    // The pass runs on every load: matching is by text, so it links the rows
    // to the bookmarks that already exist instead of creating them twice.
    return await createMissing({ reportId, priorities, rooms, bookmarks, dated, known, dismissed });
  } finally {
    inFlight.delete(String(reportId));
  }
}

async function createMissing({ reportId, priorities, rooms, bookmarks, dated, known, dismissed }) {
  const open = (bookmarks || []).filter((todo) => todo && todo.todoId != null);
  const scheduled = (dated || []).filter(Boolean);
  let roomList = Array.isArray(rooms) ? rooms : [];
  try {
    const fetched = await loadAllRooms();
    const byId = new Map();
    roomList.concat(Array.isArray(fetched) ? fetched : []).forEach((room) => {
      if (!room || room.id == null) return;
      const name = room.name || room.buildingName || "";
      if (!name && !byId.has(String(room.id))) return;
      const prev = byId.get(String(room.id));
      byId.set(String(room.id), { id: room.id, name: name || (prev && prev.name) || "" });
    });
    if (byId.size) roomList = [...byId.values()];
  } catch (_) { /* the rooms passed in still match */ }

  const todos = [];
  const pending = [];
  const repairs = [];
  const detailRepairs = [];
  const seen = [];
  (priorities || []).forEach((row) => {
    const summary = priorityText(row);
    const key = normalizeTaskText(summary);
    const roomId = roomIdOf(row, roomList);
    // Every task belongs to a project: a row without one stays report text
    // (neither created nor recorded, so a later report that names it can).
    if (!roomId) return;
    // A report that words the same step twice still gets one bookmark.
    if (!key || seen.some((other) => isSamePriority(other, key))) return;
    seen.push(key);
    // Deleted by the user: the step stays off the list, however the next
    // report words it.
    if (findSimilar(dismissed, key)) return;
    // This report already produced a bookmark for the step. Creating it again
    // would bring back a task the user has since deleted.
    const recorded = findSimilar(known, key, (entry) => entry.text);
    if (recorded) {
      const existing = open.find((todo) => String(todo.todoId) === String(recorded.id));
      if (existing && roomId) repairs.push({ todo: existing, roomId });
      if (existing) detailRepairs.push({ todo: existing, row });
      todos.push({ id: String(recorded.id), text: key });
      return;
    }
    // An overlapping report describes the same step in slightly other words.
    const match = findSimilar(open, key, (todo) => todo.description);
    if (match) {
      if (roomId) repairs.push({ todo: match, roomId });
      detailRepairs.push({ todo: match, row });
      todos.push({ id: String(match.todoId), text: key });
      return;
    }
    // Already scheduled by hand — bookmarking it again would duplicate it.
    if (findSimilar(scheduled, key, (todo) => todo.description)) return;
    pending.push({ row, summary, key, roomId });
  });

  let repaired = false;
  for (const item of repairs) {
    if (await assignRoom(item.todo, item.roomId)) repaired = true;
  }
  for (const item of detailRepairs) {
    if (await assignDetails(item.todo, item.row)) repaired = true;
  }

  let created = false;
  let failed = false;
  for (const { row, summary, key, roomId } of pending) {
    // A task, not a bookmark: the server dates it from the row's deadline or
    // its text ("bis 27.10.", "Donnerstag nächste Woche"), else today. The
    // report row (project, deadline, section) and the report itself let it
    // sort the task under Termine / Kritische Punkte / Offene Entscheidungen.
    const payload = {
      summary,
      // The task's description text under its title.
      details: rowDetails(row),
      keepSummary: true,
      reportTask: true,
      // emailId / chatId / groupId: the mail or chat the row was written from,
      // stored as the task's source.
      context: {
        source: "report",
        project: row.project || "",
        deadline: row.deadline || "",
        kind: row.kind || "",
        reportId,
        emailId: row.emailId || "",
        chatId: row.chatId || "",
        groupId: row.groupId || "",
      },
      roomId,
    };
    try {
      const { data } = await callAPI("/todo/manualTodoCreate", {}, "POST", payload);
      const todo = data && data.data;
      if ((data && data.status === false) || !todo || !todo.id) {
        failed = true;
        continue;
      }
      created = true;
      todos.push({ id: String(todo.id), text: key });
      // An API without `keepSummary` answers with its own placeholder title.
      if (normalizeTaskText(todo.description) !== key) {
        await callAPI(`/todo/${todo.id}`, {}, "PUT", { description: summary }).catch(() => {});
      }
    } catch (_) {
      failed = true;
    }
  }

  // A partial run stays unrecorded so the missing rows are retried next time.
  // Earlier entries are carried over: a row the report has dropped must stay
  // on the record, or deleting its task would let the next pass recreate it.
  if (!failed && todos.length) {
    const carried = (known || []).filter((entry) => entry && !findSimilar(todos, entry.text, (t) => t.text));
    await saveState(reportId, { todos: todos.concat(carried) });
  }
  return { created, repaired, todos, dismissed: dismissed || [] };
}
